import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/utils/api";
import { Badge } from "@/components/ui/badge";
import { Search, User, Award, Calendar, GraduationCap, X } from "lucide-react";

export default function GlobalSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const timer = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (!query || query.length < 2) { setResults(null); setOpen(false); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(query)}`);
        setResults(data);
        setOpen(true);
      } catch {}
      setLoading(false);
    }, 250);
    return () => clearTimeout(timer.current);
  }, [query]);

  const go = (path) => { setOpen(false); setQuery(""); navigate(path); };
  const total = results ? (results.members?.length || 0) + (results.certificates?.length || 0) + (results.courses?.length || 0) + (results.qualifications?.length || 0) : 0;

  return (
    <div ref={ref} className="relative w-full max-w-md" data-testid="global-search">
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search members, certs, courses..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results && setOpen(true)}
          className="w-full h-8 pl-9 pr-8 rounded-md border border-border bg-white text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          data-testid="global-search-input"
        />
        {query && (
          <button onClick={() => { setQuery(""); setOpen(false); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            <X size={14} />
          </button>
        )}
      </div>

      {open && results && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-border rounded-md shadow-lg z-50 max-h-[400px] overflow-y-auto" data-testid="search-results">
          {total === 0 ? (
            <div className="p-4 text-sm text-muted-foreground text-center">No results for "{query}"</div>
          ) : (
            <>
              {results.members?.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider bg-slate-50 border-b border-border flex items-center gap-1"><User size={10} />Members</div>
                  {results.members.map(m => (
                    <button key={m.id} onClick={() => go(`/members?open=${m.id}`)} className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between border-b border-border" data-testid={`search-member-${m.id}`}>
                      <div><p className="text-sm font-medium">{m.name}</p><p className="text-xs text-muted-foreground">{m.email} | {m.phone || "No phone"}</p></div>
                      <Badge variant="outline" className="text-[9px] capitalize">{m.role}</Badge>
                    </button>
                  ))}
                </div>
              )}
              {results.certificates?.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider bg-slate-50 border-b border-border flex items-center gap-1"><Award size={10} />Certificates</div>
                  {results.certificates.map(c => (
                    <button key={c.id} onClick={() => go(`/certificates?open=${c.member_id || ""}`)} className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between border-b border-border" data-testid={`search-cert-${c.id}`}>
                      <div><p className="text-sm font-medium">{c.member_name} — {c.qualification_name}</p><p className="text-xs text-muted-foreground">No: {c.certificate_number || "—"} | Expires: {c.expiry_date}</p></div>
                      <Badge variant="outline" className={`text-[9px] ${c.computed_status === "active" ? "bg-emerald-100 text-emerald-700" : c.computed_status === "expiring_soon" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{c.computed_status?.replace("_"," ")}</Badge>
                    </button>
                  ))}
                </div>
              )}
              {results.courses?.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider bg-slate-50 border-b border-border flex items-center gap-1"><Calendar size={10} />Courses</div>
                  {results.courses.map(c => (
                    <button key={c.id} onClick={() => go("/courses")} className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between border-b border-border" data-testid={`search-course-${c.id}`}>
                      <div><p className="text-sm font-medium">{c.title}</p><p className="text-xs text-muted-foreground">{c.start_date} | {c.location} | {c.instructor}</p></div>
                    </button>
                  ))}
                </div>
              )}
              {results.qualifications?.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider bg-slate-50 border-b border-border flex items-center gap-1"><GraduationCap size={10} />Qualifications</div>
                  {results.qualifications.map(q => (
                    <button key={q.id} onClick={() => go("/qualifications")} className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-border" data-testid={`search-qual-${q.id}`}>
                      <p className="text-sm font-medium">{q.name}</p><p className="text-xs text-muted-foreground">{q.category} | {q.code}</p>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
